import { translatableStringFactory } from "@@/shared/translations/translation";

/**
 * Validates if the given discount code value is a valid percentage.
 *
 * @param value - An object containing the type and value to be validated.
 * @param value.type - The type of the value, either "PERCENTAGE" or "VALUE".
 * @param value.value - The numerical value to be validated.
 * @returns `true` if the type is "PERCENTAGE" and the value is between 0 and 1 (inclusive),
 *          or if the type is "VALUE". Otherwise, returns `false`.
 */
const validatePercentage = (value: {
    type: "PERCENTAGE" | "VALUE";
    value: number;
}) => {
    if (value.type === "PERCENTAGE")
        return value.value >= 0 && value.value <= 1;
    return true;
};
const percentageValidationErrorMessage = translatableStringFactory({
    sv: "När rabattkoden ska ge en procentuell rabatt måste rabatten vara ett procentuellt värde mellan 0 och 100%",
    en: "When the discount code should give a percentage discount, the discount must be a percentage value between 0 and 100%",
});
/**
 * Validates the given discount code value object based on its type.
 *
 * @param value - The value object to validate.
 * @param value.type - The type of the value, either "PERCENTAGE" or "VALUE".
 * @param value.value - The numerical value to validate.
 * @returns `true` if the value is valid, otherwise `false`.
 *          For "VALUE" type, the value must be greater than or equal to 0.
 *          For "PERCENTAGE" type, the value is always considered valid.
 */
const validateValue = (value: {
    type: "PERCENTAGE" | "VALUE";
    value: number;
}) => {
    if (value.type === "VALUE") return value.value >= 0;
    return true;
};

const valueValidationErrorMessage = translatableStringFactory({
    sv: "När rabattkoden är satt att vara en rabatt till ett fast värde måste rabatten vara ett positivt nummer",
    en: "When the discount code is set to be a discount to a fixed value, the discount must be a positive number",
});

const discountCodeExtraValidations = {
    validatePercentage,
    validateValue,
    percentageValidationErrorMessage,
    valueValidationErrorMessage,
};

export { discountCodeExtraValidations };
